﻿import {
    $,
    $$,
    consentToAllCookies,
    consentToCookiePolicy,
    consentToNoCookies,
    customizeCookies,
    execWithContext,
    get,
    getCookie,
    getAndSetCookieConsents,
    gtmEvent,
    gtmSimpleEvent,
    hide,
    initForm,
    loadCss,
    parseAndCallGtmPurchaseEvent,
    postData,
    setCookie,
    show,
    toggle,
    isHidden,
    sendPromotionGtmEvent,
    Key,
    preserveUrlParameters,
    postDataJson,
    cookieConsentsLoaded
} from "@/Utils";
import {initInputListeners, validateForm} from "@/Validation"
import {addedToCart, addProtectionPackage, adjustOverviewContent} from "@/CartModule"
import {initClearComparison, initComparisonPartial, initToggleComparisonFooter} from "@/ProductModule"
import {initReCaptchas, reCaptchaCallback, reCaptchaError, reCaptchaSuccess} from "@/ReCaptcha"
import {initAddressSearch} from "@/AddressSearchCommon"
import {initManualAddressSearch} from "@/ManualAddressSearch"
import {initContactInfoFields} from "@/AccountModule"

import {initIdAndMobileLogin} from "@/sharedComponents/Header/Login"
import {closeOtherDropdowns, initDropdowns} from "@/sharedComponents/Dropdown/Dropdown"
import {closePanel, initPanels, openPanel} from "@/sharedComponents/Panel"
import {closeModal, initModals, openModalById} from "@/sharedComponents/Modal"
import {closeOtherTooltips, initTooltips} from "@/sharedComponents/Tooltip"
import {initSliders} from "@/CustomSlider.js"
import {DataAttributes, GtmEventType, PageType, ShoppingCartAction, EditCartFrom} from "@/enums"
import {EcommerceData, GtmAddToCartOptions, GtmEventWrapper, Item} from "@/interfaces"

import flatpickr from "flatpickr"
import {Estonian} from "flatpickr/dist/l10n/et"
import {english as English} from "flatpickr/dist/l10n/default"
import {Russian} from "flatpickr/dist/l10n/ru"
import {Latvian} from "flatpickr/dist/l10n/lv"
import TagManager from "react-gtm-module";

import {bindScrollAndOpen, bindToggleByClass} from "@/sharedComponents/Accordion";
import {getPhoneInstanceAndInitIfNotExists} from "@/Phone";
import {initCarousels} from "@/sharedComponents/Carousel/Carousel";

/**
 * Lisab kõigile vormidele minimaalse submit-käitumise
 */
function initForms() {
    $$("form").forEach((form: HTMLFormElement) => {
        if (form.name === "test-secure-login") return
        if (form.getAttribute("data-form-bound") !== null) {
            return
        }
        form.setAttribute("data-form-bound", "true")
        form.addEventListener("submit",
            e => {
                if (get("login-panel")?.classList.contains("register")) {
                    return
                }
                if (form.getAttribute("data-normal-form") !== null) {
                    return
                }
                e.preventDefault()
            })
    })
}

const initPhonesObserver = new IntersectionObserver(entries => {
    // kui mõni [data-phone] on nähtav, laeme phone.js'i ja bindime intlTelInputi
    if (entries.some((e) => e.isIntersecting)) {
        loadCss(window.config.cssBundles["intlPhone"], () => execWithContext(window.config.jsBundles["phone"], () => {
            entries.forEach(e => {
                getPhoneInstanceAndInitIfNotExists(e.target as HTMLInputElement)
            })
        }, true), true)
    }
}, {threshold: [0]})

export function initPhoneInputs() {
    $$(`[${DataAttributes.Phone}]`).forEach(x => {
        if (x.getAttribute(DataAttributes.PhoneBound)) {
            return
        }
        x.setAttribute(DataAttributes.PhoneBound, "true")
        // lisame kõik unbound [data-phone] elemendid observerisse
        initPhonesObserver.observe(x)
    })
}

function initAddToCart() {
    $$("[data-add-to-cart-id]").forEach(x => {
        if (x.getAttribute("data-add-to-cart-bound")) {
            return
        }
        x.setAttribute("data-add-to-cart-bound", "true")
        x.addEventListener("click", e => {
            const productId = parseInt(x.getAttribute("data-add-to-cart-id"))
            const isBonusOrder = x.getAttribute("data-bonus") === "True"
            const changeCurrencyConfirmed = x.getAttribute("data-change-currency-confirmed") === "True"
            const button = e.currentTarget as HTMLElement
            button.querySelectorAll("use").forEach(x => x.setAttribute("href", "#cart-icon-active"))
            if (button.classList.contains("add-to-cart")) {
                button.classList.add("add-to-cart--active")
            }
            const isShoppingCart = !!get("shoppingCart")
            const editCartFrom = parseInt(x.getAttribute("data-add-to-cart-from"))
            const relatedProductId = parseInt(x.getAttribute("data-parent-product-id"))
            const data = {
                productId: productId,
                includePanel: true,
                isBonusOrder: isBonusOrder,
                changeCurrencyConfirmed: changeCurrencyConfirmed,
                isShoppingCart: isShoppingCart,
                editCartFrom: editCartFrom,
                relatedProductId: relatedProductId
            }
            const callback = (resp: Response) => {
                return addedToCart(resp, button)
            }
            void postData(window.config.addToCartUrl, data, callback)
        })
    })
}

function initAddProtectionPackage() {
    const section = $("[data-protection-package]");
    if (!section) return;

    const buttons = section.querySelectorAll(".add-to-cart");
    buttons.forEach(button => {
        button.addDataBoundEventListener("click", (e: Event) => {
            buttons.forEach(button => {
                button.classList.remove('add-to-cart--active')
                button.querySelectorAll("use")[0].setAttribute("href", "#cart-icon-idle")
                button.querySelectorAll("use")[1].setAttribute("href", "#cart-icon-hover")
            })
            const target = e.currentTarget as HTMLElement;
            target.classList.add('add-to-cart--active');
            target.querySelectorAll("use").forEach(x => x.setAttribute("href", "#cart-icon-active"))
            addProtectionPackage(section, ShoppingCartAction.ProductServiceMore, button);
        })
    })
}

/**
 * Track which product detail view was opened.
 */
function initProductImpressions() {
    $$("[data-product-container=impress]").forEach(x => {
        impressionObserver.observe(x)
    })
}

function initBannerImpressions() {
    $$("[data-type=banner-link]").forEach(x => {
        bannerObserver.observe(x)
    })
}

function initCookieConsent() {
    $$("[data-cookie-all]").forEach(x => x.addEventListener("click", consentToAllCookies))
    $$("[data-cookie-none]").forEach(x => x.addEventListener("click", consentToNoCookies))
    $$("[data-cookie-custom]").forEach(x => x.addEventListener("click", consentToCookiePolicy))
}

function initMarkOrderTrackingNotificationSeen() {
    $$("[data-order-id]").forEach(x => x.addDataBoundEventListener("click", () => {
        const orderId = parseInt(x.getAttribute("data-order-id"));
        const url = window.config.markOrderTrackingNotificationSeenUrl;
        void postData(url, orderId);
    }));
}

function initNotificationClose() {
    $$("[data-notification-close]").forEach(x => x.addDataBoundEventListener("click", async (e: Event) => {
        if (x.hasAttribute("data-message-id")) {
            const messageId = parseInt(x.getAttribute("data-message-id"));
            const url = window.config.closeWebNotificationUrl;
            await postData(url, messageId).then(() => {
                x.closest(".alert").remove();
            })
        } else {
            (e.currentTarget as HTMLElement).closest(".alert").remove();
        }
        adjustOverviewContent();
    }))
}

export function startLoadingAnimation() {
    const loadingOverlay = get("loading-overlay")
    loadingOverlay.classList.add("loading-overlay--active")
    window.loadingAnimationTimeout = window.setTimeout(() => {
        if (loadingOverlay.classList.contains("loading-overlay--active")) {
            loadingOverlay.classList.add("loading-overlay--dark")
            get("loading-animation").classList.add("global-loader--active")
        }
    }, 300)
}

export function stopLoadingAnimation() {
    window.clearTimeout(window.loadingAnimationTimeout)
    const loadingOverlay = get("loading-overlay")
    if (loadingOverlay) {
        loadingOverlay.classList.remove("loading-overlay--active")
        loadingOverlay.classList.remove("loading-overlay--dark")
    }
    get("loading-animation")?.classList.remove("global-loader--active")
}

function initDateValueChanged() {
    $$(".dropdown").forEach(ddl => {
        ddl.addDataBoundEventListener("valueChange", (e: Event) => {
            const dateInput = (e.currentTarget as HTMLElement).closest(".date-input")
            const day = parseInt((dateInput.querySelector(".date-input-day input") as HTMLInputElement).value)
            const month = parseInt((dateInput.querySelector(".date-input-month input") as HTMLInputElement).value)
            const year = parseInt((dateInput.querySelector(".date-input-year input") as HTMLInputElement).value)

            const id = dateInput.getAttribute("data-dateid")
            const input = get(id) as HTMLInputElement
            input.value = `${year}-${(`0${month}`).slice(-2)}-${(`0${day}`).slice(-2)}`
        })
    })
}

function closeSearch() {
    const searchActive = $(".autocomplete__search-result--active")
    if (searchActive) {
        searchActive.classList.remove("autocomplete__search-result--active");
        $(".autocomplete__overlay").classList.remove("autocomplete__overlay--active");
    }
}

function bodyClickListener(e: Event) {
    $$(".autocomplete-list").forEach(x => {
        if (!x.hasAttribute("keep-open")) {
            x.innerHTML = ""
        }
    })

    // Dropdown
    const dropdowns = $$(".dropdown")
    if (dropdowns.length) {
        const target = e.target as Element
        const targetDropdown = target.closest(".dropdown")
        closeOtherDropdowns(targetDropdown, dropdowns)
    }

    // Tooltip
    const target = e.target as Element
    const tooltip = target.closest(".tooltip__wrapper")?.querySelector("[data-tooltip]")
    closeOtherTooltips(tooltip)

    const evTarget = e.target as HTMLElement

    // Close modal
    const modalActive = $(".modal--active")
    if (modalActive) {
        if (evTarget.classList.contains("close") || evTarget.classList.contains("close-modal") ||
            !evTarget.closest(".modal__content") &&
            !evTarget.classList.contains("openModal") &&
            !evTarget.closest(".product-video") &&
            !evTarget.classList.contains("gm-ui-hover-effect") // Google-i kaart
        ) {
            closeModal()
        }
    }

    // Close overlay
    const overlayActive = $(".panel__overlay--active")
    if (overlayActive) {
        if (evTarget.classList.contains("panel__overlay--active")) {
            closePanel()
        }
    }

    // Close search
    if (!evTarget.closest("#searchForm")) {
        closeSearch()
    }
}

function resizeListener() {
    initSliders()
}

function documentListener() {
    document.addEventListener("keyup", e => {
        if (e.key !== Key.Escape) return;
        if ($(".modal--active")) {
            closeModal()
        }
        closeSearch()
    })
}

function initCustomizeCookies() {
    // TODO: remove button[data-role='revoke-cookie-consent'] after static page modifications
    $$("button[data-role='revoke-cookie-consent']").forEach(x => {
        x.addDataBoundEventListener("click", () => {
            customizeCookies("CookieConsent")
            window.location.reload()
        })
    })

    if(!getCookie("CookieConsent")){
        openModalById("cookie-declaration-modal");
    }

    get("modify-cookies")?.addDataBoundEventListener("click", ()=> {
        const settings = get("cookie-settings");
        const settingsButtons = get("cookie-settings-buttons");
        const info = get("cookie-info");
        const infoButtons = get("cookie-info-buttons");

        [settings, settingsButtons, info, infoButtons].forEach(e => {
            toggle(e);
        })

    });
}

function initImpersonation() {
    const searchInput = get("impersonate") as HTMLInputElement
    if (!searchInput) return;
    searchInput.addDataBoundEventListener("keyup", (e: KeyboardEvent) => {
        if (e.key !== Key.Enter || searchInput.value.length < 5) return;
        void postData(window.config.changeImpersonationUrl + "?email=" + searchInput.value, null, () => {
            window.location.reload()
        })
    })
    const endImpersonation = get("end-impersonation")
    endImpersonation?.addDataBoundEventListener("click", () => {
        void postData(window.config.endImpersonationUrl, null, () => {
            window.location.reload()
        })
    })
}

function initSearchFocus() {
    if (get("noSearchAutoFocus")) return;
    if (window.isResponsive && get("Home")) return;

    const q = new URLSearchParams(window.location.search)
    if (q.get("p") === "1") return;
    const searchInput = get('searchKeyword') as HTMLInputElement
    searchInput?.focus()
}

function initGoogleTagManager() {
    if (!window.config.googleTagManagerKey) return;
    TagManager.initialize({
        gtmId: window.config.googleTagManagerKey,
        auth: window.config.googleTagManagerAuth,
        preview: window.config.googleTagManagerEnv,
        dataLayer:{
            userId: window.currentUser.UserId ? window.currentUser.UserId.toString() : null,
        }
    })
    $$("[data-gtm-event]").forEach(x => parseAndCallGtmPurchaseEvent(x))
    if (get(PageType.NotFound)) {
        gtmSimpleEvent(GtmEventType.MissingPage, window.location.pathname)
    }
    initGtmEvents();
}

function initOmnisend() {
    if (!getAndSetCookieConsents().marketing) return;

    window.omnisend = window.omnisend || []
    window.omnisend.push(["accountID", window.config.omnisendAccountId])
    window.omnisend.push(["track", "$pageViewed"])

    const omnisendCookieName = "omnisendContactID"
    const omnisendCookieHasContent = getCookie(omnisendCookieName)?.length
    if (window.currentUser.IsLoggedIn) {
        if (!omnisendCookieHasContent) {
            setCookie(omnisendCookieName, window.currentUser.OmnisendId, 7);
        }
    } else if (omnisendCookieHasContent) {
        // If anonymous user but omnisendContactID exists.
        customizeCookies(omnisendCookieName);
    }

    !((): any => {
        const e = document.createElement("script")
        e.type = "text/javascript"
        e.async = !0
        e.defer = !0
        e.src = "https://omnisnippet1.com/inshop/launcher-v2.js"
        document.head.appendChild(e)
    })()
}

/**
 * Kuna datepicker'eid on (veel?) üsna vähe, siis ei kutsu seda afterload'is välja, vaid vajaduspõhiselt käsitsi
 */
export function initDatePickers() {
    const datePickers = $$("[data-datepicker]")
    if (!datePickers.length) return
    const lang = $("html").getAttribute("lang")
    switch (lang) {
        case "et":
            flatpickr.localize(Estonian)
            break
        case "en":
            flatpickr.localize(English)
            break
        case "ru":
            flatpickr.localize(Russian)
            break
        case "lv":
            flatpickr.localize(Latvian)
            break
    }
    //disableMobile vajadus: https://github.com/flatpickr/flatpickr/issues/1318
    loadCss(window.config.cssBundles["pageFlatpickr"], () => {
        datePickers.forEach((el: HTMLElement) => flatpickr(el,
            {
                disableMobile: true,
                onChange: (_selectedDates, _dateStr, instance) => {
                    instance.element.classList.remove("input-validation-error")
                }
            }))
    }, true)
}

function initEcommerceInternal(p: HTMLElement) {
    p.setAttribute("data-product-container", "")
    const ecommerceData = (p.dataset as unknown) as EcommerceData
    if (!ecommerceData.productId || ecommerceData.productId == '0'){
        // Additional warranties are not rebuyable.
        return;
    }
    if (!ecommerceData.productName){
        ecommerceData.productName = p.querySelector("[data-product-name]")?.textContent.trim();
    }
    if (!ecommerceData.productCode) {
        ecommerceData.productCode = p.querySelector("[data-product-code]").textContent.trim()
    }
    if (!ecommerceData.productListName){
        ecommerceData.productListName = EditCartFrom[ecommerceData.productListId]
    }
    const categories = ecommerceData.productCategoryTree?.split("/")
    const item = {
        itemName: ecommerceData.productName,
        itemId: ecommerceData.productCode,
        price: ecommerceData.productPrice,
        itemBrand: ecommerceData.productBrand,
        itemCategory: categories[0],
        itemCategory2: categories[1],
        itemCategory3: categories[2],
        itemListId: ecommerceData.productListId,
        itemListName: ecommerceData.productListName,
        index: ecommerceData.productPosition,
        quantity: 1,
        productId: Number(ecommerceData.productId),
        currency: 'EUR',
    } as Item;
    const categoryData = $("#categoryData")?.dataset
    if (categoryData) {
        item.promotionId = parseInt(categoryData.campaignId)
        item.promotionName = categoryData.categoryName
        item.creativeName = categoryData.bannerName
        item.creativeSlot = categoryData.bannerSlot === "0" ? null : categoryData.bannerSlot
    }
    gtmEvent(GtmEventType.ViewItem, null, null, null, null, {
        eventName: GtmEventType.ViewItem,
        value: ecommerceData.productPrice,
        itemsGa4: [item]
    } as GtmAddToCartOptions)
}

const bannerObserver = new IntersectionObserver(entries => {
    const urlWithoutQuery = window.location.href.split('?')[0];
    const isPromotionsPage = urlWithoutQuery == window.config.promotionsUrl;
    entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const bannerAnchor = entry.target as HTMLElement
        bannerObserver.unobserve(bannerAnchor)
        const bannerImage = bannerAnchor.querySelector("img");
        const bannerType = bannerImage.getAttribute("data-type");
        // Slider banners views we track in Slider.ts.
        // In promotions page we actually don't have a slider.
        if (bannerType !== 'FrontpageSlider' || isPromotionsPage) {
            const eventTypeForViewing = isPromotionsPage ?
                GtmEventType.ViewPromotionCampaignHub : GtmEventType.ViewPromotion;
            sendPromotionGtmEvent(bannerImage, eventTypeForViewing);
        }
        bannerAnchor.addDataBoundEventListener("click", (e) =>{
            e.preventDefault();
            const eventTypeForSelecting = isPromotionsPage ?
                GtmEventType.SelectPromotionCampaignHub : GtmEventType.SelectPromotion
            sendPromotionGtmEvent(bannerImage, eventTypeForSelecting, true);
            const bannerId = bannerImage.getAttribute("data-id");
            document.cookie = `bannerId=${bannerId}`;
            window.location.href = ((e.target as HTMLElement).closest("a") as HTMLAnchorElement).href;
        });
    })
})

const impressionObserver = new IntersectionObserver(entries => {
    entries.forEach(e => {
        if (!e.isIntersecting) return;
        const p = e.target as HTMLElement
        impressionObserver.unobserve(p)
        initEcommerceInternal(p);
    })
}, {threshold: [1]})

const responsiveObserver = new IntersectionObserver(entries => {
    entries.forEach(e => {
        window.isResponsive = e.isIntersecting
        if (window.responsiveObserverFunctions) {
            window.responsiveObserverFunctions.forEach(f => f())
        }
    })
}, {
    threshold: [1]
})

function initResponsiveSentinel() {
    const responsiveSentinel = get("responsive-sentinel")
    window.isResponsive = window.getComputedStyle(responsiveSentinel).display !== "none";
    responsiveObserver.observe(responsiveSentinel);
}

function initFooter() {
    if (window.currentUser.IsLoggedIn) {
        return
    }
    const subscribeBtn = get("SubscribeNewsletter") as HTMLButtonElement;
    if (!subscribeBtn) {
        return
    }
    const captcha = subscribeBtn.parentNode.querySelector('.reCaptcha') as HTMLElement;
    const formId = "subscribe-form";

    // Hide captcha if success
    captcha.addDataBoundEventListener('captchaSuccess', () => {
        const isNotVisibleReCaptcha = isHidden(captcha);
        if (!isNotVisibleReCaptcha) {
            subscribeBtn.click();
        }
    }, 'successListener');

    const handleResponse = async function (resp: Response) {
        function resetCaptcha() {
            if (window.grecaptcha) {
                captcha.removeAttribute(DataAttributes.ReCaptchaInitialized)
                const widgetId = captcha.querySelector('textarea').id
                const captchaIdNr = parseInt(widgetId[widgetId.length - 1])
                if (captchaIdNr) {
                    window.grecaptcha.reset(captchaIdNr)
                } else {
                    window.grecaptcha.reset(0)
                }
            }
        }

        let data = await resp.json();
        if (data.ok) {
            get('subscribe-form').innerHTML = data.message
        } else {
            resetCaptcha();
            subscribeBtn.addDataBoundEventListener('click', onClickCallback)
            get('subscribeMessage').innerHTML = data.message
        }
    }
    const onClickCallback = (e: Event) => {
        e.preventDefault();
        const formValid = validateForm(get(formId) as HTMLFormElement)
        if (formValid) {
            show(captcha)
            hide(subscribeBtn)
            hide(get('subscribeMessage'));
            e.target.removeEventListener('click', onClickCallback)
            initForm(formId, handleResponse);
        } else {
            // Show email validation error
            show(get('subscribeMessage'));
        }

    };
    subscribeBtn.addDataBoundEventListener('click', onClickCallback)
}

export function initGtmEvents(events?: string) {
    let res: GtmEventWrapper;
    if (!events) {
        const gtmContainer = $("#GtmEvents");
        if (!!gtmContainer) {
            events = gtmContainer.textContent;
            //Remove the current events from DOM so that they would not be sent twice when initGtmEvents is triggered again
            gtmContainer.remove();
        }
    }

    if (!!events) {
        if (typeof events === 'string') {
            res = JSON.parse(events) as GtmEventWrapper;
        } else {
            res = events as GtmEventWrapper;
        }

        res.events.forEach(data => {
            TagManager.dataLayer({
                dataLayer: {
                    event: data.name,
                    ...data.params
                }
            });
        });
    }
    if (window.mustSetAnalyticsIds){
        TagManager.dataLayer({
            dataLayer: {
                event: "set_analytics_ids_in_backend",
                eventCallback: setAnalyticsIds,
                eventTimeout: 2000
            }
        });
    }
}

function initCustomZendeskWidgetButton() {
    $$(".open-zendesk").forEach(x =>
        x.addDataBoundEventListener("click", () => {
            if (typeof window.zE === "undefined")
                return;
            window.zE('webWidget', 'open');
        }));
}

function initNotifyPageError(){

    const elem = get("btn-notify-page-error");
    elem?.addEventListener("click",
        () => {
            openPanel('notify-page-error-panel');
        });

    window.responsiveObserverFunctions.push(updateNotifyPageErrorBtnLocation)
}

function updateNotifyPageErrorBtnLocation(){
    const btn = $('#btn-notify-page-error') as HTMLButtonElement;
    if (!btn) return;

    const pricingBlock = $('.pricing-block__bottom');
    const comparisonBlock = $('.comparison-footer__container');

    const initialBottomVal = 10;
    const pricingBlockHeight = pricingBlock && window.isResponsive ? pricingBlock.clientHeight : 0;
    const comparisonBlockHeight = comparisonBlock && window.isResponsive ? comparisonBlock.clientHeight : 0;

    btn.style.bottom = (initialBottomVal + pricingBlockHeight + comparisonBlockHeight).toString() + 'px';
}

export function initShowMore() {
    document.querySelectorAll("h5.view-more").forEach(element => {
        element.addDataBoundEventListener('click', () => showMore(element as HTMLDivElement));
    });
    const showMore = (element: HTMLDivElement) => {
        const section = element.parentElement;
        for (const card of Array.from(section.children)) {
            (card as HTMLElement).style.display = 'grid';
        }

        section.removeChild(element);
    }
}

function setAnalyticsIds() {
    if (!window.mustSetAnalyticsIds)
        return;
    window.mustSetAnalyticsIds = false;
    try {
        Promise.all([
            new Promise(resolve => window.gtag('get', window.config.googleMeasurementId, 'client_id', resolve)),
            new Promise(resolve => window.gtag('get', window.config.googleMeasurementId, 'session_id', resolve)),
        ])
            .then(([clientId, sessionId]) => {
                void postDataJson(window.config.setAnalyticsIdsUrl, {clientId, sessionId});
            })
    } catch (_) {
        // Non-critical error. Ignoring it.
    }
}

/**
 * Kõik funktsioonid, mis peavad käima minema lehe laadimisel ja pärast igat fetch'i
 *
 * Siin ei tohi olla koodi, mis ei tohi joosta mitu korda.
 * Näiteks addEventListener asemel kasutada addDataBoundEventListener
 */
export function afterLoad(withLoadingAnimation: boolean = true) {
    initForms()
    initInputListeners()
    initPhoneInputs()
    initAddToCart()
    initProductImpressions()
    initBannerImpressions()
    initIdAndMobileLogin(true)
    initIdAndMobileLogin(false)
    bindToggleByClass()
    bindScrollAndOpen()
    initComparisonPartial()
    initCookieConsent()
    initMarkOrderTrackingNotificationSeen()
    initNotificationClose()
    initModals()
    initPanels()
    window.reCaptchaCallback = reCaptchaCallback
    window.reCaptchaSuccess = reCaptchaSuccess
    window.reCaptchaError = reCaptchaError
    if (withLoadingAnimation) {
        stopLoadingAnimation()
    }
    initDropdowns()
    initDateValueChanged()
    initAddressSearch()
    initManualAddressSearch()
    initContactInfoFields()
    window.initZendeskWidget()
    initCustomZendeskWidgetButton()
    if (window.config.search.nostoSearchUrl.length) {
        window.initNostoTracker()
    } else {
        window.initSearchNodeTracker()
    }
    initCustomizeCookies()
    initImpersonation()
    initTooltips()
    initReCaptchas()
    initFooter()
    initToggleComparisonFooter()
    initClearComparison()
    initAddProtectionPackage()
    initShowMore()
    initCarousels()
    initNotifyPageError()
}

/**
 * Kõik funktsioonid, mis peavad käima minema lehe laadimisel
 */
window.addEventListener("load", () => {
    initResponsiveSentinel()
    getAndSetCookieConsents()
    window.dispatchEvent(new Event(cookieConsentsLoaded))
    afterLoad()
    document.body.addEventListener("click", bodyClickListener)
    window.addEventListener("resize", resizeListener)
    documentListener()
    initGoogleTagManager()
    initSearchFocus()
    initOmnisend()
    window.dispatchEvent(new Event("omnisendInitialized"))
    preserveUrlParameters()
    if (window.klaviyoIdentify) {
        window.klaviyoIdentify()
    }
})